import React from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import distinctColors from "distinct-colors";
import moment from "moment/moment";
import { useTheme } from "@mui/material";
import { InsightsResponse, Platform } from "../api/v1/api_pb";
import { timestampDate } from "@bufbuild/protobuf/wkt";

interface SchedulingProps {
  readonly insights: InsightsResponse;
  readonly dateFormat: string;
  readonly platforms: readonly Platform[];
}

function Scheduling(props: SchedulingProps) {
  let theme = useTheme();

  const dataKeys = new Set<string>([
    "Max time to start a job",
    "Mean time to start a job",
  ]);

  const data = props.insights.aggregates.map((aggregate) => {
    let queueTimeSecondsMax = 0;
    let queueTimeSecondsSum = 0;
    let queueTimeSecondsCount = 0;

    for (const metrics of aggregate.platformMetrics) {
      if (!props.platforms.includes(metrics.platform)) {
        continue;
      }

      queueTimeSecondsMax = Math.max(
        queueTimeSecondsMax,
        metrics.queueTimeSeconds?.max ?? 0,
      );
      queueTimeSecondsSum += metrics.queueTimeSeconds?.sum ?? 0;
      queueTimeSecondsCount += metrics.queueTimeSeconds?.count ?? 0;
    }

    return {
      date: moment(timestampDate(aggregate.startAt!)).format(props.dateFormat),
      "Max time to start a job": moment
        .duration(queueTimeSecondsMax, "seconds")
        .asMinutes(),
      "Mean time to start a job": moment
        .duration(queueTimeSecondsSum / queueTimeSecondsCount, "seconds")
        .asMinutes(),
    };
  });

  // Generate a palette of distinct colors with limited chroma
  // to prevent hard to see colors and bleeding eyes and limited
  // hue to use less warm colors in our cold-colored UI
  //
  // A handy tool for customizing this library: https://medialab.github.io/iwanthue/
  const palette = distinctColors({
    count: dataKeys.size,
    hueMin: 75,
    chromaMin: 25,
    chromaMax: 75,
  });

  const bars = Array.from(dataKeys).map((dataKey) => (
    <Area
      key={dataKey}
      dataKey={dataKey}
      type="monotone"
      fill={palette.pop()!.hex()}
    />
  ));

  return (
    <ResponsiveContainer width="100%" height={350}>
      <AreaChart data={data}>
        <XAxis dataKey="date" />
        <YAxis />
        <CartesianGrid strokeDasharray="3 3" />
        <Tooltip
          contentStyle={{ backgroundColor: theme.palette.background.paper }}
          formatter={(value) => `${Number(value).toFixed(0)} minutes`}
        />
        {bars}
      </AreaChart>
    </ResponsiveContainer>
  );
}

export default Scheduling;
